"use client";

import { ArrowRight, Cpu, MousePointerClick, ShieldCheck, Sparkles } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const featureCards = [{
  number: "01",
  title: "Trust in 3 seconds",
  description: "First impressions dictate bounce rates. Clear positioning and instant authority keep high-value prospects on your site.",
  icon: ShieldCheck
}, {
  number: "02",
  title: "Conversion architecture",
  description: "Every page element is engineered to remove friction and direct potential clients into your booking system.",
  icon: MousePointerClick
}, {
  number: "03",
  title: "Machine-readable structure",
  description: "Structured schema and clean content hierarchy allow AI engines like ChatGPT, Gemini, and Perplexity to index and cite you.",
  icon: Cpu
}];
export default function WhyItMatters() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="31002" id="whyitmattersfeatures" className="bg-background py-24 md:py-36 text-foreground relative overflow-hidden">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div ref={ref} className={`grid grid-cols-1 md:grid-cols-12 gap-12 lg:gap-16 items-center transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="md:col-span-7 flex flex-col items-start gap-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-[hsl(352_54%_33%/0.12)] border border-[hsl(352_54%_33%/0.25)] text-[hsl(352_54%_33%)]">
              <Sparkles className="size-3.5" />
              <span className="font-mono text-xs font-semibold tracking-[0.25em] uppercase">
                WHY IT MATTERS
              </span>
            </div>

            <h2 className="font-default text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight">
              Looking good is not enough. Your site must <span className="italic text-[hsl(352_54%_33%)]">perform</span> everywhere.
            </h2>

            <p className="font-sans text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl">
              A modern digital presence requires more than sleek aesthetics. To convert high-intent traffic, your platform must establish immediate trust, guide visitors effortlessly toward conversion, and maintain flawless search engine readability.
            </p>

            <p className="font-sans text-base text-muted-foreground leading-relaxed max-w-2xl">
              Crucially, web discovery has evolved. AI search assistants like ChatGPT, Gemini, and Perplexity parse web data directly to provide service recommendations. Without a machine-readable architecture, your business remains invisible to the fastest-growing audience online.
            </p>

            <div className="pt-4 flex flex-wrap gap-4 items-center">
              <Button asChild size="lg" className="bg-[hsl(352_54%_33%)] hover:bg-[hsl(352_54%_26%)] text-white shadow-md rounded-lg font-medium px-6 py-3 transition-colors duration-200">
                <Link to="/PricingPlansPage"><span>Get started now</span><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-arrow-right size-4 ml-1" aria-hidden="true"><path d="M5 12h14"></path><path d="m12 5 7 7-7 7"></path></svg></Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="rounded-lg border-border hover:border-[hsl(352_54%_33%)] transition-colors duration-200">
                <Link to="/ContactPage">Get a free AI checkup</Link>
              </Button>
            </div>
          </div>

          <div className="md:col-span-5 flex flex-col gap-5">
            {featureCards.map((card, i) => {
            const IconComponent = card.icon;
            return <div key={card.number} data-index={i} className="group relative border border-border rounded-2xl p-6 bg-[hsl(240_10%_7%)] text-white shadow-md hover:border-[hsl(352_54%_33%)] hover:-translate-y-1 transition-all duration-300">
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <span className="font-mono text-xs font-semibold tracking-wider text-[hsl(352_54%_45%)]">
                      {card.number} /
                    </span>
                    <div className="p-2.5 rounded-lg bg-[hsl(0_0%_100%/0.06)] group-hover:bg-[hsl(352_54%_33%/0.2)] text-[hsl(352_54%_45%)] group-hover:text-[hsl(352_54%_60%)] transition-colors duration-200">
                      <IconComponent className="size-5" />
                    </div>
                  </div>

                  <h3 className="font-default text-lg font-semibold text-white mb-2 group-hover:text-[hsl(352_54%_70%)] transition-colors duration-200">
                    {card.title}
                  </h3>

                  <p className="font-sans text-xs sm:text-sm text-gray-300 leading-relaxed">
                    {card.description}
                  </p>
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}